const MOBILE_MEDIA_QUERY = '(max-width: 767px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const AUTOPLAY_DELAY = 5000;
const SWIPE_THRESHOLD = 36;

export const initSiteTopbar = (topbar) => {
    if (!(topbar instanceof HTMLElement) || topbar.dataset.topbarReady === 'true') {
        return null;
    }

    const track = topbar.querySelector('[data-topbar-track]');
    const slides = [...topbar.querySelectorAll('[data-topbar-slide]')];

    if (!track || slides.length === 0) {
        return null;
    }

    const mobileQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    let activeIndex = 0;
    let autoplayTimer = 0;
    let pointerStartX = null;
    let paused = false;

    slides.forEach((slide) => slide.setAttribute('draggable', 'false'));
    track.addEventListener('dragstart', (event) => event.preventDefault());

    const stopAutoplay = () => {
        window.clearTimeout(autoplayTimer);
        autoplayTimer = 0;
    };

    const canAutoplay = () => (
        mobileQuery.matches
        && !reducedMotionQuery.matches
        && !paused
        && !document.hidden
        && slides.length > 1
    );

    const syncAccessibility = () => {
        slides.forEach((slide, index) => {
            const isActive = index === activeIndex;

            if (mobileQuery.matches) {
                slide.setAttribute('aria-hidden', String(!isActive));
                slide.inert = !isActive;
            } else {
                slide.removeAttribute('aria-hidden');
                slide.inert = false;
            }

            slide.classList.toggle('is-active', isActive);
        });

    };

    const scheduleAutoplay = () => {
        stopAutoplay();

        if (!canAutoplay()) {
            return;
        }

        autoplayTimer = window.setTimeout(() => {
            setActive(activeIndex + 1);
        }, AUTOPLAY_DELAY);
    };

    const setActive = (requestedIndex, { restart = true } = {}) => {
        activeIndex = (requestedIndex + slides.length) % slides.length;
        track.style.setProperty('--topbar-offset', `${activeIndex * -100}%`);
        syncAccessibility();

        if (restart) {
            scheduleAutoplay();
        }
    };

    const setPaused = (nextPaused) => {
        paused = nextPaused;

        if (paused) {
            stopAutoplay();
        } else {
            scheduleAutoplay();
        }
    };

    topbar.addEventListener('keydown', (event) => {
        if (!mobileQuery.matches || slides.length < 2) {
            return;
        }

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault();
            setActive(activeIndex + 1);
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault();
            setActive(activeIndex - 1);
        }
    });

    topbar.addEventListener('pointerdown', (event) => {
        if (mobileQuery.matches && event.isPrimary) {
            pointerStartX = event.clientX;
        }
    }, { passive: true });

    topbar.addEventListener('pointerup', (event) => {
        if (pointerStartX === null || !mobileQuery.matches || !event.isPrimary) {
            pointerStartX = null;
            return;
        }

        const distance = event.clientX - pointerStartX;
        pointerStartX = null;

        if (Math.abs(distance) < SWIPE_THRESHOLD) {
            return;
        }

        setActive(activeIndex + (distance < 0 ? 1 : -1));
    }, { passive: true });

    topbar.addEventListener('pointercancel', () => {
        pointerStartX = null;
    });
    topbar.addEventListener('mouseenter', () => setPaused(true));
    topbar.addEventListener('mouseleave', () => setPaused(false));
    topbar.addEventListener('focusin', () => setPaused(true));
    topbar.addEventListener('focusout', (event) => {
        if (!(event.relatedTarget instanceof Node) || !topbar.contains(event.relatedTarget)) {
            setPaused(false);
        }
    });

    const handleEnvironmentChange = () => {
        setActive(activeIndex, { restart: false });
        scheduleAutoplay();
    };

    mobileQuery.addEventListener?.('change', handleEnvironmentChange);
    reducedMotionQuery.addEventListener?.('change', handleEnvironmentChange);
    document.addEventListener('visibilitychange', handleEnvironmentChange);

    setActive(0);
    topbar.dataset.topbarReady = 'true';

    return {
        get activeIndex() {
            return activeIndex;
        },
        goTo: (index) => setActive(index),
        destroy: () => {
            stopAutoplay();
            mobileQuery.removeEventListener?.('change', handleEnvironmentChange);
            reducedMotionQuery.removeEventListener?.('change', handleEnvironmentChange);
            document.removeEventListener('visibilitychange', handleEnvironmentChange);
            topbar.removeAttribute('data-topbar-ready');
        },
    };
};
